// "Email Clerk" and "Email Instructor" on the Fee Slips & Invoices page.
//
// On the page they were only statuses: choosing one recorded it and sent nothing. Deadline
// Guard's Client Account dashboard opens an email for them and sends it from the service's
// Gmail account (POST /api/gmail/send). The page now does the same, so the work can be done
// from the portal, with Emma's own wording (below).

import { clerkKey, clerkFromSender, counselFromSubject, senderAddress } from "./clerkMatching.js";

export const EMAIL_STATUSES = {
  email_clerk: { recipient: "clerk", sentStatus: "clerk_emailed", sentLabel: "Clerk emailed" },
  email_instructor: { recipient: "instructor", sentStatus: "instructor_emailed", sentLabel: "Instructor emailed" },
};

export const GMAIL_SEND_PATH = "/api/gmail/send";

export const isEmailStatus = (status) => Object.prototype.hasOwnProperty.call(EMAIL_STATUSES, status);

// A statement row's invoice name is its number; a fee slip or an emailed invoice keeps the
// number read off it separately.
export function invoiceNumberOf(row = {}) {
  const ref = String(row.invoiceRef || "").trim();
  if (ref) return ref;
  return row.source === "clerk_statement" ? String(row.invoiceName || "").trim() : "";
}

// The clerk's accounts address, from the emails that clerk has sent: the row's own email if
// it came from one, otherwise the latest invoice emailed by the same clerk.
export function clerkAddressFor(row = {}, rows = []) {
  const own = row.rawEmail?.sender;
  if (own && clerkFromSender(own)) return senderAddress(own);
  const key = clerkKey(row.clerkName);
  if (!key) return "";
  const sent = rows
    .filter((other) => other?.source === "email" && clerkKey(other.clerkName) === key)
    .map((other) => ({ sender: other.rawEmail?.sender || "", at: String(other.uploadedAt || "") }))
    .filter((other) => clerkFromSender(other.sender))
    .sort((a, b) => b.at.localeCompare(a.at));
  return sent.length ? senderAddress(sent[0].sender) : "";
}

// Emma's template (16 Oct 2026):
//   Subject: Fee Slip 06012309 - Matter of Shannon Hurren - Counsel: Katrina Hartman
//   Good morning, (or Good afternoon, from 12pm - set again when the email is sent)
//   one of: a request for a copy, a request to amend it (with her wording), or her own text
//   Kind regards,
//   Emma
export const EMAIL_REQUESTS = [
  { value: "copy", label: "Ask for a copy of the fee slip" },
  { value: "amend", label: "Ask for the fee slip to be amended" },
  { value: "blank", label: "Write my own message" },
];

export const DEFAULT_SIGN_OFF = "Emma";

export const greetingFor = (date = new Date()) => (date.getHours() < 12 ? "Good morning" : "Good afternoon");

// Placeholders the page shows when it does not know a name; never put in an email.
const NO_NAME = /^(?:counsel not specified|unknown counsel|emailed|client not yet named|unnamed client)$/i;

// "HURREN, SHANNON" -> "Shannon Hurren"; "NED BAMAGA" -> "Ned Bamaga"; "O'BRIEN" -> "O'Brien".
// A name already in mixed case is left as it was typed.
export function personName(value = "") {
  let name = String(value || "").replace(/\s+/g, " ").trim();
  if (!name || NO_NAME.test(name)) return "";
  const parts = name.split(",").map((part) => part.trim()).filter(Boolean);
  if (parts.length === 2) name = `${parts[1]} ${parts[0]}`;
  if (name !== name.toUpperCase()) return name;
  return name.toLowerCase().replace(/(^|[\s'’-])([a-z])/g, (_, before, letter) => before + letter.toUpperCase());
}

// Counsel for the line. An emailed invoice only knows counsel when its subject names them
// ("Invoice 00157164 for Anna Dearman in the matter of ..."); its sender is the clerk.
export function counselOf(row = {}) {
  if (row.source === "email") return personName(counselFromSubject(row.rawEmail?.subject || ""));
  return personName(row.counselName);
}

export function emailSubject(row = {}) {
  const number = invoiceNumberOf(row);
  const client = personName(row.clientName);
  const counsel = counselOf(row);
  return [
    `Fee Slip${number ? ` ${number}` : ""}`,
    client && `Matter of ${client}`,
    counsel && `Counsel: ${counsel}`,
  ].filter(Boolean).join(" - ");
}

export function emailBody({ request = "copy", number = "", freeText = "", date = new Date(), signOff = DEFAULT_SIGN_OFF } = {}) {
  const slip = number ? `fee slip ${number}` : "the fee slip";
  const text = String(freeText || "").trim();
  let message;
  if (request === "amend") message = `Can ${slip} please be amended to the following:${text ? `\n\n${text}` : "\n\n"}`;
  else if (request === "blank") message = text;
  else message = `Can a copy of ${slip} please be provided?`;
  return [`${greetingFor(date)},`, "", message, "", "Kind regards,", String(signOff || DEFAULT_SIGN_OFF).trim()].join("\n");
}

// The greeting is set for the time the email is actually sent, not when it was opened.
export function withCurrentGreeting(body = "", date = new Date()) {
  return String(body).replace(/^Good (?:morning|afternoon),/, `${greetingFor(date)},`);
}

export function clerkEmailDraft({ row = {}, status, rows = [], request = "copy", freeText = "", date = new Date(), signOff } = {}) {
  const mode = EMAIL_STATUSES[status];
  if (!mode) return null;
  return {
    to: mode.recipient === "clerk" ? clerkAddressFor(row, rows) : "",
    cc: "",
    bcc: "",
    subject: emailSubject(row),
    body: emailBody({ request, number: invoiceNumberOf(row), freeText, date, signOff }),
  };
}

const auDate = (date) => {
  const d = date instanceof Date ? date : new Date(date);
  return `${String(d.getDate()).padStart(2, "0")}/${String(d.getMonth() + 1).padStart(2, "0")}/${d.getFullYear()}`;
};

// Once the email has gone: "Email Clerk" becomes "Clerk Emailed", and the notes say when
// and to whom ("Clerk emailed 16/10/2026 to holmesaccounts@vicbar.com.au."), so the date
// stays with the line. Returns the row's new { status, notes }.
export function afterEmailSent({ status, notes = "", to = "", date = new Date() } = {}) {
  const mode = EMAIL_STATUSES[status];
  if (!mode) return { status, notes };
  const line = `${mode.sentLabel} ${auDate(date)}${String(to).trim() ? ` to ${String(to).trim()}` : ""}.`;
  return { status: mode.sentStatus, notes: [String(notes || "").trim(), line].filter(Boolean).join("\n") };
}

const splitAddresses = (value) => String(value || "").split(/[,;]/).map((part) => part.trim()).filter(Boolean);
const looksLikeAddress = (value) => /^[^\s@<>]+@[^\s@<>]+\.[^\s@<>]+$/.test(value);

// Returns an error message, or "" when the email can be sent.
export function emailProblem({ to, cc, bcc, subject, body } = {}) {
  const recipients = splitAddresses(to);
  if (!recipients.length) return "Enter an email address first.";
  const bad = [...recipients, ...splitAddresses(cc), ...splitAddresses(bcc)].find((address) => !looksLikeAddress(address));
  if (bad) return `"${bad}" is not an email address.`;
  if (!String(subject || "").trim()) return "Add a subject.";
  if (!String(body || "").trim()) return "The email is empty.";
  return "";
}

// Sends through the signing proxy (vercel.json routes /api/gmail/* there). The service
// answers ok with delivered:false when Gmail refused and it kept the email in its outbox:
// that is reported as not sent, so nobody believes the clerk has it.
export async function sendClerkEmail(message, fetchImpl = globalThis.fetch) {
  const problem = emailProblem(message);
  if (problem) throw new Error(problem);
  const response = await fetchImpl(GMAIL_SEND_PATH, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      to: splitAddresses(message.to).join(", "),
      cc: splitAddresses(message.cc).join(", "),
      bcc: splitAddresses(message.bcc).join(", "),
      subject: String(message.subject).trim(),
      body: String(message.body),
      // { filename, mimeType, data (base64) }, as Deadline Guard's mail builder takes them.
      ...(Array.isArray(message.attachments) && message.attachments.length ? { attachments: message.attachments } : {}),
    }),
  });
  let payload = null;
  try {
    payload = await response.json();
  } catch {
    payload = null;
  }
  if (!response.ok || payload?.ok === false) {
    throw new Error(payload?.error || `The email could not be sent (${response.status}).`);
  }
  if (payload?.delivered === false) {
    throw new Error(`Gmail did not send it${payload.warning ? `: ${payload.warning}` : ""}. It was kept in Deadline Guard's outbox, not delivered.`);
  }
  return payload || { ok: true };
}
